import * as React from 'react';

import MiniSearch from 'minisearch';

import type { FlatTierRow } from './useEditablePricingTable';

type SearchableItem = {
  id: string;
  itemName: string;
  pricingUnit: string;
};

const MINISEARCH_THRESHOLD = 2;

export function usePricingSearch(flatTierRows: FlatTierRow[]) {
  const [searchQuery, setSearchQuery] = React.useState('');

  const searchableItems = React.useMemo(() => {
    const seen = new Set<string>();
    const items: SearchableItem[] = [];
    for (const row of flatTierRows) {
      if (seen.has(row.itemPricingId)) continue;
      seen.add(row.itemPricingId);
      items.push({
        id: row.itemPricingId,
        itemName: row.itemName,
        pricingUnit: row.pricingUnit,
      });
    }
    return items;
  }, [flatTierRows]);

  const miniSearch = React.useMemo(() => {
    const ms = new MiniSearch<SearchableItem>({
      fields: ['itemName', 'pricingUnit'],
      storeFields: ['id'],
      searchOptions: {
        fuzzy: 0.2,
        prefix: true,
        combineWith: 'AND',
        boost: { itemName: 2 },
      },
    });
    if (searchableItems.length > 0) {
      ms.addAll(searchableItems);
    }
    return ms;
  }, [searchableItems]);

  const matchedItemPricingIds = React.useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) return null;

    if (trimmed.length < MINISEARCH_THRESHOLD) {
      const ids = new Set<string>();
      for (const item of searchableItems) {
        const haystack = `${item.itemName} ${item.pricingUnit}`.toLowerCase();
        if (haystack.includes(trimmed)) {
          ids.add(item.id);
        }
      }
      return ids;
    }

    const results = miniSearch.search(searchQuery);
    return new Set(results.map((r) => r.id));
  }, [searchQuery, miniSearch, searchableItems]);

  const filteredRows = React.useMemo(() => {
    if (!matchedItemPricingIds) return flatTierRows;
    return flatTierRows.filter((row) =>
      matchedItemPricingIds.has(row.itemPricingId),
    );
  }, [flatTierRows, matchedItemPricingIds]);

  return {
    searchQuery,
    setSearchQuery,
    filteredRows,
    isFiltered: matchedItemPricingIds !== null,
  };
}
